import { useEffect, useState, type FormEvent } from "react";
type Staff = {
  id: string;
  email: string;
  staff_email: string | null;
  display_name: string;
  disabled_at: string | null;
};
export function StaffUsers({
  api,
  mutate,
}: {
  api: <T>(path: string) => Promise<T>;
  mutate: <T>(path: string, body: unknown) => Promise<T>;
}) {
  const [users, setUsers] = useState<Staff[]>([]),
    [attempt, setAttempt] = useState(0),
    [createMailbox, setCreateMailbox] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [invitation, setInvitation] = useState(""),
    [setupRecipient, setSetupRecipient] = useState(""),
    [setupLogin, setSetupLogin] = useState(""),
    [setupHasMail, setSetupHasMail] = useState(false),
    [copied, setCopied] = useState(false);
  const setupMessage = `Your CRTC account is ready to set up.

Open this link within 24 hours and choose your password:
${invitation}

Your sign-in address is ${setupLogin}. ${setupHasMail ? "After setup, sign in to CRTC and open Staff Email, then Open webmail. You do not need access to your new company inbox to activate your account." : "After setup, sign in to CRTC with your new password."}`;
  useEffect(() => {
    let active = true;
    api<{ items: Staff[] }>("/admin/staff-users")
      .then((data) => {
        if (active) setUsers(data.items);
      })
      .catch((cause) => {
        if (active) setError(cause.message);
      });
    return () => {
      active = false;
    };
  }, [api, attempt]);
  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setInvitation("");
    setCopied(false);
    const login = String(form.get("email")).trim();
    const recipient = String(form.get("contactEmail")).trim();
    if (createMailbox && recipient.toLowerCase() === login.toLowerCase()) {
      setError(
        "Use an existing email address they can already open, not the new company mailbox.",
      );
      setBusy(false);
      return;
    }
    try {
      const result = await mutate<{ invitationUrl: string }>(
        "/admin/staff-users/invitations",
        { email: String(form.get("email")), createMailbox },
      );
      setSetupRecipient(recipient);
      setSetupLogin(login);
      setSetupHasMail(createMailbox);
      setInvitation(result.invitationUrl);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function link(event: FormEvent<HTMLFormElement>, user: Staff) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await mutate("/admin/staff-users/" + user.id + "/email", {
        address: String(new FormData(event.currentTarget).get("address")),
      });
      setAttempt((x) => x + 1);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="cr-panel">
      <h2>Staff accounts & email</h2>
      <p>
        One CRTC sign-in for administration, company email, calendars, and
        contacts.
      </p>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      <ul className="cr-service-status-list">
        {users.map((user) => (
          <li key={user.id}>
            <div>
              <strong>{user.display_name}</strong>
              <p>{user.staff_email || user.email}</p>
              <p className="cr-form-note">
                {user.disabled_at ? "Disabled" : "Administrator"}
                {user.staff_email
                  ? " · Company email enabled"
                  : " · No company mailbox"}
              </p>
              {!user.disabled_at && !user.staff_email && (
                <form
                  className="cr-form"
                  onSubmit={(event) => void link(event, user)}
                >
                  <label>
                    Company email address
                    <input
                      name="address"
                      type="email"
                      required
                      placeholder="name@cacherivertelephone.com"
                    />
                  </label>
                  <button className="cr-button secondary" disabled={busy}>
                    Enable company email
                  </button>
                </form>
              )}
            </div>
          </li>
        ))}
      </ul>
      <details>
        <summary>Add staff user</summary>
        <p>
          Create their sign-in, then give them a setup link using an email
          address they already have. They choose their password first and open
          their company mailbox afterward. This account grants administrator
          access.
        </p>
        <form className="cr-form" onSubmit={invite}>
          <label>
            Existing contact email
            <input
              name="contactEmail"
              type="email"
              required
              placeholder="An address they can already open"
            />
          </label>
          <p className="cr-form-note">
            Used to address the setup message. It does not become their company
            mailbox.
          </p>
          <label>
            <input
              type="checkbox"
              checked={createMailbox}
              onChange={(event) => setCreateMailbox(event.target.checked)}
            />
            Include a new company mailbox
          </label>
          <label>
            {createMailbox ? "New company email address" : "Sign-in email"}
            <input
              name="email"
              type="email"
              required
              placeholder={
                createMailbox ? "name@cacherivertelephone.com" : "Email address"
              }
            />
          </label>
          <button className="cr-button" disabled={busy}>
            {busy ? "Creating…" : "Create setup link"}
          </button>
        </form>
        {invitation && (
          <div role="status">
            <p>
              Setup link ready for {setupRecipient}. Nothing has been sent yet.
              Copy the message or open an email draft addressed to their
              existing inbox. The link expires in 24 hours and works once.
            </p>
            <ol>
              <li>Send the setup link to {setupRecipient}.</li>
              <li>They open the link and choose their own CRTC password.</li>
              <li>
                They sign in as {setupLogin}
                {setupHasMail
                  ? " and open company webmail from Staff Email."
                  : "."}
              </li>
            </ol>
            <input
              aria-label="Staff invitation link"
              value={invitation}
              readOnly
              onFocus={(event) => event.target.select()}
            />
            <button
              type="button"
              className="cr-button secondary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(setupMessage)
                  .then(() => setCopied(true))
                  .catch(() =>
                    setError(
                      "Copy the setup link above and share it directly.",
                    ),
                  );
              }}
            >
              {copied ? "Setup message copied" : "Copy setup message"}
            </button>
            <a
              className="cr-button secondary"
              href={`mailto:${encodeURIComponent(setupRecipient)}?subject=${encodeURIComponent("Set up your CRTC account")}&body=${encodeURIComponent(setupMessage)}`}
            >
              Open email draft
            </a>
          </div>
        )}
      </details>
    </section>
  );
}
